import React, { useEffect, useState } from 'react';
import {
    Alert, Card, Col, InputNumber, Row, Skeleton, Space, Switch, Tabs, Tag, Typography,
} from 'antd';
import { useSubsystemEditorContext } from '../hooks/useSubsystemDraftEditor';
import { SubsystemDraftActions } from '../components/SubsystemDraftActions';
import { PromptEditor } from '../shared/PromptEditor';
import {
    ProviderModelTempForm,
    ProviderModelTempValue,
    ProviderId,
} from '../shared/ProviderModelTempForm';
import { useAuth } from '../../../hooks/useAuth';
import { aiAdminApi, liveSettingsKeys } from '../../../services/ai-admin-api';
import {
    CatalogueModel,
    ModelBeliefsPanelEditor,
    PanelMember,
    parseCatalogue,
    parsePanel,
} from '../../components/model-beliefs/ModelBeliefsPanelEditor';
import {
    GatePersona,
    GatePrompt,
    ModelBeliefsGateEditor,
    parseJsonList,
} from '../../components/model-beliefs/ModelBeliefsGateEditor';

const { Text, Paragraph } = Typography;

interface ConfigRow {
    id?: number;
    config_key: string;
    system_prompt: string;
    user_prompt_template: string;
    is_active?: boolean | number;
    [k: string]: unknown;
}

interface ModelBeliefsPayload {
    schemaVersion: 1;
    systemSettingsKeys: Record<string, string | undefined>;
    configTableRows: { model_belief_ai_config?: ConfigRow[] };
}

// The three prompts, described by what each one is FOR — a reader of this page
// is deciding whether to touch one, and the config_key alone does not tell them.
const KEYS: { configKey: string; label: string; description: string }[] = [
    {
        configKey: 'statement_read',
        label: 'Statement read',
        description:
            'The survey itself. Each call asks a model to answer a batch of the brand’s own statements twice over: as itself, and as a typical customer. Deliberately composes NO shared fragments — this call is measuring a model, not writing for a customer, and our house style and element vocabulary have no business in the instrument. The mode line is the only difference between the searched pass and the memory pass.',
    },
    {
        configKey: 'awareness_gate',
        label: 'Awareness gate',
        description:
            'A category question asked before any belief, because a brand that is never listed never has its drivers evaluated. It MUST NOT name the brand: handed the name, a model lists the name and inclusion reads near 100%. {consumer_name} is not resolved for this prompt, and a brand name appearing here fails the gate rather than corrupting it.',
    },
    {
        configKey: 'page_read',
        label: 'Page read',
        description:
            'v4. One page the rank question read, fetched by us and handed to the reader model with search off, scored against six of the brand’s statements at a time: 1 to 7 from the page alone, or not covered, with the words it rests on. Placeholders: {consumer_name}, {disambiguation}, {market}, {country_name}, {page_url}, {page_title}, {page_text}, {statements}, {scale}, {count}. Composes no shared fragments: it measures a page, it does not write for a customer. Until this box is published the page read is skipped.',
    },
    {
        configKey: 'category_suggest',
        label: 'Category suggest',
        description:
            'v4. Admin only, from the Brands tab: reads the brand’s homepage and asks the read’s model, with web search, for up to five product categories a shopper searches for separately, one marked main. A person picks one as the category label. Placeholders: {brand_name}, {site}, {country_name}, {market}, {site_text}. Composes no shared fragments. Until this box is published the built-in text is used.',
    },
    {
        configKey: 'competitor_suggest',
        label: 'Competitor suggest',
        description:
            'Admin only, from the Brands tab and the ad-hoc read form: reads the brand’s homepage and asks the read’s model, with web search, for up to eight brands a shopper would weigh it against when buying the category. A person picks which to read. Placeholders: {brand_name}, {site}, {category}, {country_name}, {market}, {count}, {site_text}. Composes no shared fragments. Until this box is published the built-in text is used.',
    },
    {
        configKey: 'synthesis',
        label: 'Synthesis',
        description:
            'The customer-facing read. The only prompt here that composes the shared fragments, because it is the only one writing for a person. Banned from arithmetic — every number it quotes was computed for it — and backed by mechanical checks on computed gaps, audience framing and invented citations.',
    },
    {
        configKey: 'synthesis_review',
        label: 'Synthesis review',
        description:
            'v4. Checks the written read against its input, sentence by sentence: the right words on the right statement, numbers from one statement, record fix grounds, and a model’s belief stated as fact. It reports problems and never rewrites the read; the writer fixes them in one retry. Placeholders: {input_text}, {draft_json}. Composes no shared fragments. Runs only when the Review the read switch is on and this box is published.',
    },
];

// Grouped because these change what a read MEANS, not how fast it runs, and
// they version with the prompts for that reason.
const NUMERIC_SETTINGS: { key: string; label: string; help: string; min?: number; max?: number; step?: number }[] = [
    { key: 'MODEL_BELIEFS_N_FLOOR', label: 'Samples per statement', help: 'How many times each statement is put to each model, in each retrieval mode, before top-up — the precision floor of the whole read. Measured on run 30: at 8 the interval target is met for under half the statements on the thinking models, and none of Gemini’s.', min: 1, max: 40 },
    { key: 'MODEL_BELIEFS_N_CAP', label: 'Sample cap', help: 'The ceiling top-up may raise a single statement to when its interval stays wide.', min: 1, max: 100 },
    { key: 'MODEL_BELIEFS_BATCH_SIZE', label: 'Batch size (fallback)', help: 'Statements per call for any panel member that sets none. Per-member sizes override it, because tolerance for a crowded call is a property of the model.', min: 1, max: 20 },
    { key: 'MODEL_BELIEFS_CI_HALF_WIDTH_TARGET', label: 'CI half-width target', help: 'The precision top-up chases: half the 95% interval on the customer view, in Likert points.', min: 0.05, max: 2, step: 0.05 },
    { key: 'MODEL_BELIEFS_TOPUP_BUDGET_FACTOR', label: 'Top-up allowance', help: 'Extra calls a cell may spend chasing that target, as a fraction of its planned calls — so the worst case is plan x (1 + this).', min: 0, max: 1, step: 0.05 },
    { key: 'MODEL_BELIEFS_GATE_N', label: 'Gate replicates', help: 'How many times each gate question is asked, per prompt x persona x model x mode. The rank question (v3) is one prompt, one persona, 60 replicates: 360 calls on a three-model panel with the memory pass, the same as the old battery.', min: 1, max: 120 },
    { key: 'MODEL_BELIEFS_PAGE_MIN_ANSWERS', label: 'Pages: minimum answers', help: 'A page is fetched and read only when at least this many searched rank answers read it. At 2, a page one answer happened on is kept in the source ledger but not read.', min: 1, max: 60 },
    { key: 'MODEL_BELIEFS_PAGE_MAX_CHARS', label: 'Pages: characters read', help: 'How much of each page the reader is shown. Run 1001’s lab read used 8,000, which holds a whole review or listicle and keeps a page call to a few thousand tokens.', min: 1000, max: 40000, step: 1000 },
    { key: 'MODEL_BELIEFS_SUGGEST_RUNS', label: 'Suggestion runs', help: 'How many times the category and competitor suggestion question is put to the model, at once, before the lists are merged. Each suggestion shows how many runs named it. One run on Koala missed sofas; three at about ten cents each do not.', min: 1, max: 6 },
    { key: 'MODEL_BELIEFS_MAX_SEARCHES', label: 'Max searches per call', min: 1, max: 20, help: 'The server-side web-search budget handed to the model on a searching call.' },
    { key: 'MODEL_BELIEFS_NO_BELIEF_RATE_THRESHOLD', label: 'No-belief threshold', help: 'At or above this share of “I hold nothing here” answers, the cell reports no belief and carries no number, rather than averaging shrugs into a confident-looking middle.', min: 0, max: 1, step: 0.05 },
    { key: 'MODEL_BELIEFS_MOVEMENT_THRESHOLD', label: 'Movement threshold', help: 'Likert points a mean must shift, with non-overlapping intervals, before two reads count as having moved.', min: 0, max: 3, step: 0.1 },
    { key: 'MODEL_BELIEFS_MAX_CALLS_PER_RUN', label: 'Max calls per run', help: 'Hard spend stop. The planner scales n down to fit and records that it did.', min: 1, max: 10000 },
    { key: 'MODEL_BELIEFS_DAILY_CAP', label: 'Runs per day', min: 1, max: 100, help: 'Platform-wide guard on runs started per day. Runs above it wait rather than fail.' },
    { key: 'MODEL_BELIEFS_REFRESH_DAYS', label: 'Refresh every (days)', help: 'How stale a brand’s last read must be before the scheduler re-reads it.', min: 1, max: 365 },
    { key: 'MODEL_BELIEFS_BRAND_READ_MAX_AGE_DAYS', label: 'Pairing limit (days)', help: 'How old a brand read may be and still be paired with a competitor-only run. Past it, the comparison measures the calendar.', min: 1, max: 365 },
    { key: 'MODEL_BELIEFS_MEMORY_N_FACTOR', label: 'Memory-pass n factor', help: 'The retrieval-off pass samples at this fraction of n. Cheap, and it is where the memory-versus-record gap comes from.', min: 0.1, max: 1, step: 0.1 },
    { key: 'MODEL_BELIEFS_COMPETITOR_N_FACTOR', label: 'Competitor n factor', help: 'Competitor targets sample at this fraction of n, since they are context rather than the subject.', min: 0.1, max: 1, step: 0.1 },
    { key: 'MODEL_BELIEFS_MAX_COMPETITORS', label: 'Competitors per read', help: 'How many configured competitors actually get read. The rest are dropped, and the run records which ones.', min: 0, max: 10 },
    { key: 'MODEL_BELIEFS_MIN_SAMPLES_OK', label: 'Minimum samples per cell', help: 'Below this a cell is not reported at all: a mean of one answer is not a measurement.', min: 1, max: 20 },
    { key: 'MODEL_BELIEFS_CALL_TIMEOUT_MS', label: 'Call timeout (ms)', help: 'Per-call wall clock. Too low and the slowest model drops out of the panel rather than answering.', min: 10000, max: 600000, step: 10000 },
    { key: 'MODEL_BELIEFS_MANUAL_COOLDOWN_DAYS', label: 'Manual cooldown (days)', help: 'How long a customer waits between self-served reads, because the record does not move in a day.', min: 0, max: 90 },
    { key: 'MODEL_BELIEFS_SYNTHESIS_MAX_TOKENS', label: 'Synthesis max tokens', help: 'Output ceiling for the written read. Too low truncates it mid-section.', min: 1000, max: 64000, step: 1000 },
];

// The switches that change what a run READS rather than how it is sized.
// Each is stated in full because turning one off silently removes a whole
// leg of the study. defaultOn mirrors the API's code default: a switch with
// no saved setting must show what the API will actually do, or a publish
// that "leaves it on" saves nothing and the API reads it as off.
const PASS_SWITCHES: { key: string; label: string; help: string; defaultOn: boolean }[] = [
    {
        key: 'MODEL_BELIEFS_MEMORY_PASS',
        defaultOn: true,
        label: 'Memory pass',
        help: 'Asks every statement a second time with retrieval off. It produced the finding the product exists for — the models recommend a brand LESS after searching than from memory — and on the first full read it cost $7.60 of $183.53. Off saves almost nothing and removes the comparison.',
    },
    {
        key: 'MODEL_BELIEFS_SEARCHED_STATEMENTS',
        defaultOn: true,
        label: 'Searched statements',
        help: 'Asks every statement with web search on, as well as from memory. Off (v4) asks the statements from memory only: the searched view of the brand then comes from reading the pages the rank question surfaced, one page at a time. The rank question keeps searching either way. Off saves roughly $50 a read.',
    },
    {
        key: 'MODEL_BELIEFS_PAGE_READ',
        defaultOn: false,
        label: 'Read the pages',
        help: 'After the rank question, fetches every page its searched answers read (at least the minimum answers below) and has the panel member marked “Reads pages” score each one against the brand’s statements, search off. This is where the searched view of the brand comes from in v4. About six calls a page on the reader model, plus Firecrawl for pages we cannot fetch ourselves. Needs the Page read prompt published.',
    },
    {
        key: 'MODEL_BELIEFS_SYNTHESIS_REVIEW',
        defaultOn: false,
        label: 'Review the read',
        help: 'After the read is written, a second call checks it against its input and lists any sentence the input does not support. Anything found goes back to the writer in one retry; what is still wrong after that is saved on the run and shown in admin. About $0.15 to $0.30 a review, at most two a read. Needs the Synthesis review prompt published.',
    },
    {
        key: 'MODEL_BELIEFS_READ_COMPETITORS',
        defaultOn: true,
        label: 'Read competitors',
        help: 'Off drops the competitor leg entirely, and configured competitors then do nothing at all: the gate stops recognising them too, so a competitor a model names is reported as an unrecognised brand.',
    },
];

const isActive = (v: boolean | number | undefined): boolean => v === true || v === 1;

const guessProvider = (modelId: string | undefined | null): ProviderId => {
    if (!modelId) return 'anthropic';
    if (modelId.startsWith('claude')) return 'anthropic';
    if (modelId.startsWith('gemini')) return 'google';
    return 'openai';
};

export const ModelBeliefsAiConfigurePage: React.FC = () => {
    const editor = useSubsystemEditorContext<ModelBeliefsPayload>();
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const [catalogue, setCatalogue] = useState<Record<string, CatalogueModel[]>>({});

    // The panel picks from the same catalogue Models & Providers publishes, so
    // a model that is not in it shows as such rather than failing at call time.
    useEffect(() => {
        if (!jwt) return;
        aiAdminApi
            .getSubsystem(jwt, 'models_providers')
            .then((mp) => {
                const keys = liveSettingsKeys(mp);
                setCatalogue({
                    anthropic: parseCatalogue(keys.AVAILABLE_ANTHROPIC_MODELS),
                    openai: parseCatalogue(keys.AVAILABLE_OPENAI_MODELS),
                    google: parseCatalogue(keys.AVAILABLE_GOOGLE_MODELS),
                });
            })
            .catch(() => {
                // Non-fatal: a model id can still be picked from what is set.
            });
    }, [jwt]);

    if (editor.isLoading || !editor.payload) {
        return <Skeleton active paragraph={{ rows: 12 }} />;
    }

    const settings = editor.payload.systemSettingsKeys ?? {};
    const rows = editor.payload.configTableRows?.model_belief_ai_config ?? [];
    const enabled = settings.MODEL_BELIEFS_ENABLED === 'true';

    const setSetting = (key: string, value: string) =>
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, [key]: value },
        }));

    const upsertRow = (configKey: string, patch: Partial<ConfigRow>) =>
        editor.setPayload((prev) => {
            const list = prev!.configTableRows.model_belief_ai_config ?? [];
            const idx = list.findIndex((r) => r.config_key === configKey);
            const next =
                idx >= 0
                    ? list.map((r, i) => (i === idx ? { ...r, ...patch } : r))
                    : [...list, { config_key: configKey, system_prompt: '', user_prompt_template: '', is_active: 1, ...patch }];
            return { ...prev!, configTableRows: { ...prev!.configTableRows, model_belief_ai_config: next } };
        });

    const findRow = (configKey: string) => rows.find((r) => r.config_key === configKey);

    const setManySettings = (patch: Record<string, string>) =>
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, ...patch },
        }));

    // Stored as JSON text in a system setting, edited here as structure. Written
    // back pretty-printed because the next person to read it may well be reading
    // it in System Settings rather than on this page.
    const panel = parsePanel(settings.MODEL_BELIEFS_PANEL);
    const setPanel = (next: PanelMember[]) =>
        setSetting('MODEL_BELIEFS_PANEL', JSON.stringify(next, null, 2));

    const gatePrompts = parseJsonList<GatePrompt>(settings.MODEL_BELIEFS_GATE_PROMPTS);
    const gatePersonas = parseJsonList<GatePersona>(settings.MODEL_BELIEFS_GATE_PERSONAS);

    const synthesisPrimary: ProviderModelTempValue = {
        provider:
            (settings.MODEL_BELIEFS_AI_PRIMARY_PROVIDER as ProviderId) ||
            guessProvider(settings.MODEL_BELIEFS_AI_PRIMARY_MODEL),
        model: settings.MODEL_BELIEFS_AI_PRIMARY_MODEL ?? '',
        temperature: Number(settings.MODEL_BELIEFS_AI_PRIMARY_TEMPERATURE ?? 0.3),
    };

    const synthesisFallback: ProviderModelTempValue = {
        provider:
            (settings.MODEL_BELIEFS_AI_FALLBACK_PROVIDER as ProviderId) ||
            guessProvider(settings.MODEL_BELIEFS_AI_FALLBACK_MODEL),
        model: settings.MODEL_BELIEFS_AI_FALLBACK_MODEL ?? '',
        temperature: Number(settings.MODEL_BELIEFS_AI_FALLBACK_TEMPERATURE ?? 0.3),
    };

    const suggestPrimary: ProviderModelTempValue = {
        provider:
            (settings.MODEL_BELIEFS_SUGGEST_AI_PRIMARY_PROVIDER as ProviderId) ||
            guessProvider(settings.MODEL_BELIEFS_SUGGEST_AI_PRIMARY_MODEL),
        model: settings.MODEL_BELIEFS_SUGGEST_AI_PRIMARY_MODEL ?? '',
        temperature: Number(settings.MODEL_BELIEFS_SUGGEST_AI_PRIMARY_TEMPERATURE ?? 0.3),
    };

    const suggestFallback: ProviderModelTempValue = {
        provider:
            (settings.MODEL_BELIEFS_SUGGEST_AI_FALLBACK_PROVIDER as ProviderId) ||
            guessProvider(settings.MODEL_BELIEFS_SUGGEST_AI_FALLBACK_MODEL),
        model: settings.MODEL_BELIEFS_SUGGEST_AI_FALLBACK_MODEL ?? '',
        temperature: Number(settings.MODEL_BELIEFS_SUGGEST_AI_FALLBACK_TEMPERATURE ?? 0.3),
    };

    return (
        <div>
            <Space direction="vertical" size={4} style={{ width: '100%', marginBottom: 16 }}>
                <Text type="secondary">
                    What ChatGPT, Claude and Gemini believe about a brand, measured with the brand&rsquo;s own survey statements.
                </Text>
                <SubsystemDraftActions
                    detail={editor.detail}
                    isDirty={editor.isDirty}
                    isSaving={editor.isSaving}
                    isPublishing={editor.isPublishing}
                    isDiscarding={editor.isDiscarding}
                    onSaveDraft={() => editor.saveDraft()}
                    onDiscardDraft={() => editor.discardDraft()}
                />
            </Space>

            <Card title="Status" size="small" style={{ marginBottom: 16 }}>
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Space>
                        <Switch
                            checked={enabled}
                            onChange={(checked) => setSetting('MODEL_BELIEFS_ENABLED', checked ? 'true' : 'false')}
                            checkedChildren="on"
                            unCheckedChildren="off"
                        />
                        <Tag color={enabled ? 'green' : 'default'}>{enabled ? 'live for customers' : 'off'}</Tag>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        The master switch does not apply to a platform admin or an impersonation session, so the
                        whole surface can be exercised on real brands while it is dark. The plan entitlement still
                        applies to those sessions &mdash; testing dark is about the switch, not about ignoring what a
                        plan includes. With it off the scheduler enqueues nothing.
                    </Text>
                </Space>
            </Card>

            <ModelBeliefsPanelEditor
                value={panel}
                onChange={setPanel}
                catalogue={catalogue}
                batchSizeFallback={settings.MODEL_BELIEFS_BATCH_SIZE}
                nFloorFallback={settings.MODEL_BELIEFS_N_FLOOR}
            />

            <ModelBeliefsGateEditor
                prompts={gatePrompts}
                personas={gatePersonas}
                onPromptsChange={(next) =>
                    setSetting('MODEL_BELIEFS_GATE_PROMPTS', JSON.stringify(next, null, 2))
                }
                onPersonasChange={(next) =>
                    setSetting('MODEL_BELIEFS_GATE_PERSONAS', JSON.stringify(next, null, 2))
                }
                gateN={Number(settings.MODEL_BELIEFS_GATE_N ?? 10)}
                panelSize={panel.filter((m) => m.enabled !== false && m.provider && m.model).length}
                memoryPass={settings.MODEL_BELIEFS_MEMORY_PASS !== 'false'}
            />

            <Card title="Passes" size="small" style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]}>
                    {PASS_SWITCHES.map((sw) => {
                        const saved = settings[sw.key];
                        const on = saved === undefined || saved === '' ? sw.defaultOn : saved !== 'false';
                        return (
                            <Col key={sw.key} xs={24} lg={12}>
                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    <Space>
                                        <Switch
                                            checked={on}
                                            onChange={(checked) =>
                                                setSetting(sw.key, checked ? 'true' : 'false')
                                            }
                                            checkedChildren="on"
                                            unCheckedChildren="off"
                                        />
                                        <Text strong style={{ fontSize: 13 }}>{sw.label}</Text>
                                        <Tag color={on ? 'green' : 'default'}>{on ? 'read' : 'skipped'}</Tag>
                                    </Space>
                                    <Text type="secondary" style={{ fontSize: 11 }}>{sw.help}</Text>
                                </Space>
                            </Col>
                        );
                    })}
                </Row>
            </Card>

            <Card title="The model that writes the read" size="small" style={{ marginBottom: 16 }}>
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Not one of the models being measured"
                    description="The panel above is the instrument. This is the model that turns a finished run into the written read, with every number already computed for it — it is banned from arithmetic, so its job is prose, not measurement."
                />
                <ProviderModelTempForm
                    label="Primary"
                    value={synthesisPrimary}
                    onChange={(next) =>
                        setManySettings({
                            MODEL_BELIEFS_AI_PRIMARY_PROVIDER: next.provider,
                            MODEL_BELIEFS_AI_PRIMARY_MODEL: next.model,
                            MODEL_BELIEFS_AI_PRIMARY_TEMPERATURE: String(next.temperature),
                        })
                    }
                    anthropicModels={catalogue.anthropic ?? []}
                    openaiModels={catalogue.openai ?? []}
                    googleModels={catalogue.google ?? []}
                />
                <div style={{ marginTop: 12 }}>
                    <ProviderModelTempForm
                        label="Fallback"
                        value={synthesisFallback}
                        onChange={(next) =>
                            setManySettings({
                                MODEL_BELIEFS_AI_FALLBACK_PROVIDER: next.provider,
                                MODEL_BELIEFS_AI_FALLBACK_MODEL: next.model,
                                MODEL_BELIEFS_AI_FALLBACK_TEMPERATURE: String(next.temperature),
                            })
                        }
                        anthropicModels={catalogue.anthropic ?? []}
                        openaiModels={catalogue.openai ?? []}
                        googleModels={catalogue.google ?? []}
                    />
                </div>
            </Card>

            <Card title="The model that suggests a brand's setup" size="small" style={{ marginBottom: 16 }}>
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Category, competitors and price positioning"
                    description="The Suggest buttons on a brand's read settings put one searched question to this model several times and merge the answers. It is a search-and-summarise job, so the search engine matters more than the prose, and a person confirms every suggestion before a read starts. Nothing here touches the written read."
                />
                <ProviderModelTempForm
                    label="Primary"
                    value={suggestPrimary}
                    onChange={(next) =>
                        setManySettings({
                            MODEL_BELIEFS_SUGGEST_AI_PRIMARY_PROVIDER: next.provider,
                            MODEL_BELIEFS_SUGGEST_AI_PRIMARY_MODEL: next.model,
                            MODEL_BELIEFS_SUGGEST_AI_PRIMARY_TEMPERATURE: String(next.temperature),
                        })
                    }
                    anthropicModels={catalogue.anthropic ?? []}
                    openaiModels={catalogue.openai ?? []}
                    googleModels={catalogue.google ?? []}
                />
                <div style={{ marginTop: 12 }}>
                    <ProviderModelTempForm
                        label="Fallback"
                        value={suggestFallback}
                        onChange={(next) =>
                            setManySettings({
                                MODEL_BELIEFS_SUGGEST_AI_FALLBACK_PROVIDER: next.provider,
                                MODEL_BELIEFS_SUGGEST_AI_FALLBACK_MODEL: next.model,
                                MODEL_BELIEFS_SUGGEST_AI_FALLBACK_TEMPERATURE: String(next.temperature),
                            })
                        }
                        anthropicModels={catalogue.anthropic ?? []}
                        openaiModels={catalogue.openai ?? []}
                        googleModels={catalogue.google ?? []}
                    />
                </div>
            </Card>

            <Card title="Sampling" size="small" style={{ marginBottom: 16 }}>
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="These are versioned with the prompts on purpose"
                    description="They change what a read MEANS, not merely how fast it runs. Restoring a prompt version without the instrument that produced it would give you a version whose text and numbers disagree."
                />
                <Row gutter={[16, 16]}>
                    {NUMERIC_SETTINGS.map((s) => (
                        <Col key={s.key} xs={24} sm={12} lg={8}>
                            <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                <Text strong style={{ fontSize: 13 }}>{s.label}</Text>
                                <InputNumber
                                    id={s.key}
                                    style={{ width: '100%' }}
                                    min={s.min}
                                    max={s.max}
                                    step={s.step ?? 1}
                                    value={settings[s.key] !== undefined ? Number(settings[s.key]) : undefined}
                                    onChange={(v) => setSetting(s.key, v === null || v === undefined ? '' : String(v))}
                                />
                                {s.help && (
                                    <Text type="secondary" style={{ fontSize: 11 }}>{s.help}</Text>
                                )}
                            </Space>
                        </Col>
                    ))}
                </Row>
            </Card>

            <Card title="Prompts" size="small">
                <Tabs
                    items={KEYS.map((k) => {
                        const row = findRow(k.configKey);
                        return {
                            key: k.configKey,
                            label: k.label,
                            children: (
                                <div>
                                    <Paragraph type="secondary" style={{ fontSize: 13 }}>
                                        {k.description}
                                    </Paragraph>

                                    {k.configKey === 'awareness_gate' && (
                                        <Alert
                                            type="warning"
                                            showIcon
                                            style={{ marginBottom: 12 }}
                                            message="This prompt must never name a brand"
                                            description="Every rendered gate question is checked against the brand name, its domains and every competitor before the call is made. A name in a question skips that question; a name here fails the gate for the whole run. That is deliberate: a gate handed the brand reports near-total inclusion, which looks like a finding and is not one."
                                        />
                                    )}
                                    {k.configKey === 'synthesis' && (
                                        <Alert
                                            type="info"
                                            showIcon
                                            style={{ marginBottom: 12 }}
                                            message="Hard constraints belong in the FINAL CHECK, after {input_text}"
                                            description="A rule stated once above a long input loses to the obvious number in it. Three rules have already had to be moved there after being ignored: lead with the record gap, never say &ldquo;existing customers&rdquo;, and cite only domains present in the input."
                                        />
                                    )}

                                    {row && (
                                        <Space style={{ marginBottom: 12 }} wrap>
                                            <Tag color={isActive(row.is_active) ? 'green' : 'default'}>
                                                {isActive(row.is_active) ? 'active' : 'inactive'}
                                            </Tag>
                                            <Switch
                                                checked={isActive(row.is_active)}
                                                onChange={(checked) => upsertRow(k.configKey, { is_active: checked ? 1 : 0 })}
                                                checkedChildren="on"
                                                unCheckedChildren="off"
                                            />
                                        </Space>
                                    )}

                                    <div style={{ marginBottom: 16 }}>
                                        <PromptEditor
                                            consumerSubsystem="model_beliefs_ai"
                                            label="System Prompt"
                                            value={row?.system_prompt ?? ''}
                                            onChange={(v) => upsertRow(k.configKey, { system_prompt: v })}
                                            rows={14}
                                        />
                                    </div>
                                    <div>
                                        <PromptEditor
                                            consumerSubsystem="model_beliefs_ai"
                                            label="User Prompt Template"
                                            value={row?.user_prompt_template ?? ''}
                                            onChange={(v) => upsertRow(k.configKey, { user_prompt_template: v })}
                                            rows={k.configKey === 'synthesis' ? 18 : 6}
                                        />
                                    </div>
                                </div>
                            ),
                        };
                    })}
                />
                <Paragraph type="secondary" style={{ marginTop: 16, fontSize: 12 }}>
                    <code>{'{input_text}'}</code> is the block assembled in code before the call. For the
                    statement read it is the batch; for the gate it is the shopper&rsquo;s question; for the
                    synthesis it is the whole run, with every number already computed. Publishing here clears the
                    subsystem cache.
                </Paragraph>
            </Card>
        </div>
    );
};
